import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import type { GlobePlaylist } from "#shared/schemas/globe.ts";
import { cn } from "@web/lib/utils.ts";
import {
  MAX_ARCS_PER_PLAYLIST,
  MAX_SELECTED_PLAYLISTS,
  PLAYLIST_HUES,
} from "@web/features/globe/styles.ts";

/**
 * Compact horizontal chip strip: searchable dropdown + selected chips.
 * Selecting a 6th replaces the oldest.
 */
export function PlaylistPicker({
  playlists,
  selectedIds,
  onSelect,
  onDeselect,
}: {
  playlists: GlobePlaylist[];
  selectedIds: string[];
  onSelect: (id: string) => void;
  onDeselect: (id: string) => void;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const byId = useMemo(() => {
    const m = new Map<string, GlobePlaylist>();
    for (const pl of playlists) m.set(pl.id, pl);
    return m;
  }, [playlists]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = playlists.filter((pl) => !selectedIds.includes(pl.id));
    if (!q) return list.slice(0, 40);
    return list
      .filter((pl) => pl.name.toLowerCase().includes(q))
      .slice(0, 40);
  }, [playlists, selectedIds, query]);

  const selected = selectedIds
    .map((id) => byId.get(id))
    .filter((pl): pl is GlobePlaylist => Boolean(pl));

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Clears left chrome (layers + shot) and right queue.
  return (
    <div className="pointer-events-none absolute top-0 right-80 left-14 z-10 flex flex-col gap-2 py-3 pr-3">
      <div className="pointer-events-auto flex flex-wrap items-center gap-2">
        <div ref={rootRef} className="relative">
          <button
            type="button"
            className={cn(
              "flex h-8 items-center gap-1.5 rounded-md border border-line bg-panel/90 px-2.5 text-sm text-muted backdrop-blur-sm",
              "hover:border-accent/40 hover:text-text",
            )}
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={listId}
          >
            Add playlist
            <span className="text-faint">
              {selectedIds.length}/{MAX_SELECTED_PLAYLISTS}
            </span>
            <ChevronDown className="size-3.5 opacity-70" />
          </button>
          {open && (
            <div
              id={listId}
              className="absolute top-full left-0 z-20 mt-1 w-72 overflow-hidden rounded-md border border-line bg-panel-elevated shadow-lg"
              role="listbox"
            >
              <input
                autoFocus
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search playlists…"
                className="h-9 w-full border-b border-line bg-transparent px-3 text-sm text-text placeholder:text-faint focus-visible:outline-none"
              />
              <ul className="max-h-64 overflow-y-auto py-1">
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-faint">No matches</li>
                ) : (
                  filtered.map((pl) => (
                    <li key={pl.id}>
                      <button
                        type="button"
                        role="option"
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-muted hover:bg-row-hover hover:text-text"
                        onClick={() => {
                          onSelect(pl.id);
                          setQuery("");
                          setOpen(false);
                        }}
                      >
                        <Check className="size-3.5 shrink-0 opacity-0" aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{pl.name}</span>
                        <span className="shrink-0 text-xs text-faint">
                          {pl.artistCountryCodes.length} countries
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>

        {selected.map((pl, i) => {
          const hue = PLAYLIST_HUES[i % PLAYLIST_HUES.length]!;
          const extra = Math.max(0, pl.artistCountryCodes.length - MAX_ARCS_PER_PLAYLIST);
          return (
            <span
              key={pl.id}
              className="inline-flex max-w-56 items-center gap-1.5 rounded-md border border-line bg-panel/90 py-1 pr-1 pl-2 text-sm backdrop-blur-sm"
              style={{ boxShadow: `inset 3px 0 0 ${hue}` }}
            >
              <span className="truncate text-text">{pl.name}</span>
              {extra > 0 && (
                <span className="shrink-0 text-xs text-faint" title="Arcs capped at top 40 countries">
                  +{extra} more
                </span>
              )}
              <button
                type="button"
                className="rounded p-0.5 text-faint hover:bg-row-hover hover:text-text"
                aria-label={`Remove ${pl.name}`}
                onClick={() => onDeselect(pl.id)}
              >
                <X className="size-3.5" />
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
